import {MouseEvent, useLayoutEffect, useRef} from "react";
import {Container, Nav, Navbar, NavDropdown} from "react-bootstrap";
import {Link, NavLink, Outlet, useLocation, useNavigate} from "react-router";
import {useAuth} from "../auth/AuthContext";
import {useShop} from "../components/ShopContext";
import {useSiteStatus} from "../components/SiteStatusContext";
import {MaintenancePage} from "../pages/MaintenancePage";
import {ThemeToggle} from "../theme/ThemeToggle";
import {isShowcasePage} from "./showcasePages";
import {CookieNotice} from "../components/CookieNotice";
import {Avatar} from "../components/Avatar";
import {resetPageMeta} from "../components/usePageMeta";
import {common, format, rich} from "../i18n";
import {SITE_NAME} from "../seo";
import {legal} from "../legal";
import {statsPage, useEntryHit, useSiteStatsHit} from "../components/siteStats";

// What anyone can still open in maintenance mode, so administrators can sign in (as the API's MaintenanceOpenRoutes).
const MAINTENANCE_OPEN_PATHS = ['/login', '/forgot-password', '/reset-password'];

// Every page: the navbar and footer, which stay in view, and the page between them. Public and admin pages share it
// (and the login screen). In maintenance mode, anyone but an administrator sees only the maintenance message.
export function SiteLayout() {
    const {user, isAdmin, loading: sessionLoading, logout} = useAuth();
    const {open: shopOpen} = useShop();
    const {maintenance} = useSiteStatus();
    const navigate = useNavigate();
    const location = useLocation();
    const {pathname} = location;
    const main = useRef<HTMLElement>(null);
    // The home page and the products: full width (showcasePages.ts).
    const showcase = isShowcasePage(pathname);
    const previousPathname = useRef(pathname);
    // The anonymous visit counts (siteStats.ts): each page shown, and where the visit came from.
    useSiteStatsHit({ event: 'page-view', path: statsPage(pathname) }, pathname);
    useEntryHit(location);

    // A new page: back to the site name and description until the page sets its own (its effect runs after this one),
    // and focus to its heading, so keyboard and screen reader users start there rather than on the link they followed.
    // Not when the page has focused a field itself (autoFocus), or on the first page.
    useLayoutEffect(() => {
        resetPageMeta(pathname);
        if (previousPathname.current === pathname) {
            return;
        }
        previousPathname.current = pathname;
        const active = document.activeElement;
        if (active instanceof HTMLInputElement || active instanceof HTMLTextAreaElement || active instanceof HTMLSelectElement) {
            return;
        }
        const heading = main.current?.querySelector('h1');
        if (heading) {
            heading.tabIndex = -1;
            heading.focus();
        } else {
            main.current?.focus();
        }
    }, [pathname]);

    const skipToMain = (e: MouseEvent) => {
        e.preventDefault();
        main.current?.focus();
    };

    const signOut = async () => {
        await logout();
        navigate('/');
    };

    // Not while a new page is still finding out who's signed in: an administrator would see the message flash.
    if (maintenance && !sessionLoading && !isAdmin && !MAINTENANCE_OPEN_PATHS.includes(pathname)) {
        return <MaintenancePage/>;
    }

    return (
        <div className={`d-flex flex-column min-vh-100${showcase ? ' showcase-layout' : ''}`}>
            <a href="#main-content" className="skip-link visually-hidden-focusable position-absolute top-0 start-0 m-2 p-2 bg-body border rounded" onClick={skipToMain}>
                {common.layout.skipToMain}
            </a>
            <CookieNotice/>
            <Navbar expand="md" sticky="top" className="bg-body-tertiary border-bottom" collapseOnSelect>
                <Container>
                    <Navbar.Brand as={Link} to="/">
                        <img src="/favicon-48.png" alt="" width="24" height="24" className="me-2 align-text-top"/>
                        {SITE_NAME}
                    </Navbar.Brand>
                    <Navbar.Toggle aria-controls="main-nav"/>
                    <Navbar.Collapse id="main-nav">
                        <Nav className="me-auto">
                            <Nav.Link as={NavLink} to="/" end>{common.layout.nav.home}</Nav.Link>
                            <Nav.Link as={NavLink} to="/products">{common.layout.nav.products}</Nav.Link>
                            <Nav.Link as={NavLink} to="/docs">{common.layout.nav.documentation}</Nav.Link>
                            {user && <Nav.Link as={NavLink} to="/account/licenses">{common.layout.nav.myLicenses}</Nav.Link>}
                            {/* Signed in: your tickets (with New ticket); signed out: the form, which anyone can use. */}
                            <Nav.Link as={NavLink} to={user ? '/support' : '/contact'}>{user ? common.layout.nav.support : common.layout.nav.contact}</Nav.Link>
                            {isAdmin && <Nav.Link as={NavLink} to="/admin">{common.layout.nav.administration}</Nav.Link>}
                        </Nav>
                        <Nav>
                            <ThemeToggle/>
                            {user ? (
                                <NavDropdown align="end" id="account-menu" title={<><Avatar url={user.avatarUrl} size="sm" className="me-1 align-middle"/>{user.displayName || user.username}</>}>
                                    <NavDropdown.Item as={Link} to="/account/profile">{common.layout.accountMenu.profile}</NavDropdown.Item>
                                    <NavDropdown.Item as={Link} to="/account/licenses">{common.layout.accountMenu.myLicenses}</NavDropdown.Item>
                                    {shopOpen && <NavDropdown.Item as={Link} to="/account/orders">{common.layout.accountMenu.myOrders}</NavDropdown.Item>}
                                    <NavDropdown.Item as={Link} to="/support">{common.layout.accountMenu.supportTickets}</NavDropdown.Item>
                                    <NavDropdown.Divider/>
                                    <NavDropdown.Item onClick={signOut}>{common.layout.accountMenu.signOut}</NavDropdown.Item>
                                </NavDropdown>
                            ) : (
                                <Nav.Link as={NavLink} to="/login">{common.layout.nav.signIn}</Nav.Link>
                            )}
                        </Nav>
                    </Navbar.Collapse>
                </Container>
            </Navbar>
            {maintenance && isAdmin && (
                <div className="bg-warning-subtle text-warning-emphasis border-bottom py-2 small" role="status">
                    <Container>
                        <i className="bi bi-cone-striped me-1" aria-hidden="true"></i>
                        {rich(common.layout.maintenanceOn, { link: text => <Link to="/admin/settings">{text}</Link> })}
                    </Container>
                </div>
            )}
            {user?.emailUndeliverable && (
                <div className="bg-warning-subtle text-warning-emphasis border-bottom py-2 small" role="status">
                    <Container>
                        <i className="bi bi-envelope-exclamation me-1" aria-hidden="true"></i>
                        {rich(common.layout.emailUndeliverable, { link: text => <Link to="/account/profile">{text}</Link> }, { email: user.emailAddress })}
                    </Container>
                </div>
            )}
            {showcase ? (
                <main id="main-content" tabIndex={-1} ref={main} className="flex-grow-1">
                    <Outlet/>
                </main>
            ) : (
                <Container as="main" id="main-content" tabIndex={-1} ref={main} className="py-4 flex-grow-1">
                    <Outlet/>
                </Container>
            )}
            <footer className="sticky-bottom bg-body border-top py-3 text-body-secondary small">
                <Container className="d-flex flex-wrap justify-content-between gap-2">
                    <span className="d-flex flex-wrap gap-3">
                        <span>{format(common.layout.copyright, { year: new Date().getFullYear(), site: legal.companyName })}</span>
                        <Link to="/contact" className="link-secondary">{common.layout.contact}</Link>
                        <Link to="/press" className="link-secondary">{common.layout.press}</Link>
                    </span>
                    <nav aria-label={common.layout.legalNav} className="d-flex flex-wrap gap-3">
                        <Link to="/terms" className="link-secondary">{common.layout.terms}</Link>
                        <Link to="/privacy" className="link-secondary">{common.layout.privacy}</Link>
                        <Link to="/refunds" className="link-secondary">{common.layout.refunds}</Link>
                        <Link to="/cookies" className="link-secondary">{common.layout.cookies}</Link>
                        <Link to="/accessibility" className="link-secondary">{common.layout.accessibility}</Link>
                        <Link to="/ai-policy" className="link-secondary">{common.layout.aiPolicy}</Link>
                    </nav>
                </Container>
            </footer>
        </div>
    );
}
